import api from "api"
import { AuthManager } from "features/Authentication/AuthManager"

import {
  BROWSER_STORAGE_KEYS,
  IS_DEV_ENVIRONMENT,
  IS_LOCALHOST,
} from "./constants"
import { ValueOf } from "./types"

export const USER_PREFS_KEY_NAME = "userPreferences"

type Keys = ValueOf<typeof BROWSER_STORAGE_KEYS>
const SETTING_SLUG = "platform-preferences"

/**
 * Don't import this class directly.
 * Always import the singleton instance that's created at the end of this file.
 */
class _UserPreferenceManager {
  static _instance: _UserPreferenceManager
  // @ts-expect-error we don't need to assign initial values
  _currentUserPrefs: Record<Keys, any> = {}

  _enableSync = false

  /**
   * Writes are only safe once init() has loaded/migrated this user's
   * preferences. set()/remove() no-op until then, so a call site that runs
   * before init() can't create a uuid entry that makes the legacy migration
   * skip itself.
   */
  _initialized = false

  constructor() {
    if (_UserPreferenceManager._instance) {
      return _UserPreferenceManager._instance
    }
    // this.init()
    _UserPreferenceManager._instance = this
  }

  async init() {
    this._initialized = false

    const authInfo = AuthManager.getPlatformAuth()
    // Without a uuid every user would collapse into a single "undefined"
    // bucket, which is worse than the email keying this replaced.
    if (!authInfo?.user.uuid) return

    this._currentUserPrefs = this._copyLegacyEmailPreferences(
      authInfo.user.uuid,
      authInfo.user.email
    )

    try {
      const response = await api.settings.getUserPreferences()
      const prefs =
        JSON.parse(
          response.find(x => x.setting.slug === SETTING_SLUG)?.value || "null"
        ) || {}

      this._currentUserPrefs = {
        ...this._currentUserPrefs,
        ...prefs,
      }
      this._enableSync = true

      // console.log("> Turning on preferences syncing")

      this._updatePreferences(this._currentUserPrefs, { skipAPICall: true })
    } catch (e) {
      console.error("> Failed to fetch user preferences from server", e)
    }

    this._initialized = true

    // this.list()
  }

  /**
   * One-time copy from the legacy email-keyed blob into this uuid's own
   * entry. The email entry is never deleted, but it is a frozen snapshot
   * from the moment of the first migration — it is not kept in sync
   * afterwards. It is retained only so that another growthspace sharing
   * this email, migrating later on the same browser, still has that
   * snapshot to start from rather than nothing.
   *
   * Skips if a uuid entry already exists, which is safe because writes are
   * gated on `_initialized`, i.e. on init() having run this migration.
   *
   * Swallows errors so a storage failure can't block preference loading.
   */
  _copyLegacyEmailPreferences(uuid: string, email: string) {
    let existingPrefs: Record<string, any> = {}

    try {
      existingPrefs = this._getLocalPreferences()

      if (existingPrefs[email] && !existingPrefs[uuid]) {
        existingPrefs[uuid] = existingPrefs[email]
        localStorage.setItem(USER_PREFS_KEY_NAME, JSON.stringify(existingPrefs))
      }
    } catch (e) {
      console.error("> Failed to copy legacy email-keyed preferences", e)
    }

    return existingPrefs[uuid] || {}
  }

  /**
   * @returns Must always return an empty object in case of no data
   */
  _getLocalPreferences() {
    try {
      return (
        JSON.parse(localStorage.getItem(USER_PREFS_KEY_NAME) || "null") || {}
      )
    } catch (e) {
      console.error("Error parsing JSON", e)
      // Start with a clean slate if JSON is invalid
      localStorage.setItem(USER_PREFS_KEY_NAME, "null")
      return {}
    }
  }

  set(keyName: Keys, value: any) {
    // console.log(`> UserPreferenceManager: Setting ${keyName}=${value}`)
    if (!this._initialized) {
      console.warn(`> Dropping set("${keyName}") made before init()`)
      return
    }
    this._currentUserPrefs[keyName] = value
    this._updatePreferences(this._currentUserPrefs)
  }

  get(keyName: Keys) {
    return this._currentUserPrefs ? this._currentUserPrefs[keyName] : null
  }

  remove(keyName: Keys) {
    if (!this._initialized) {
      console.warn(`> Dropping remove("${keyName}") made before init()`)
      return
    }
    delete this._currentUserPrefs[keyName]
    this._updatePreferences(this._currentUserPrefs)
  }

  removeAll() {
    if (!this._initialized) {
      console.warn("> Dropping removeAll() made before init()")
      return
    }
    this._currentUserPrefs = {
      // @ts-expect-error this is fine. It's just for testing
      temp: "true",
    }
    this._updatePreferences(this._currentUserPrefs)
  }

  _updatePreferences(newPreferences: any, options?: any) {
    const authInfo = AuthManager.getPlatformAuth()
    if (!authInfo?.user.uuid) return

    // TODO: Implement this
    // if (!this._enableSync) {
    //   _checkIfAPIIsWorking()
    // }

    const existingPrefs = this._getLocalPreferences()
    existingPrefs[authInfo.user.uuid] = newPreferences
    localStorage.setItem(USER_PREFS_KEY_NAME, JSON.stringify(existingPrefs))

    if (!this._enableSync) {
      console.warn("> Preferences syncing is off")
      return
    }
    if (options?.skipAPICall) return
    if (authInfo.shadow) return

    // This is just a guard to prevent accidental syncing of empty preferences
    if (Object.keys(newPreferences).length === 0) {
      console.warn("> Not syncing empty preferences")
      return
    }

    // API Call to update prefs on backend
    api.settings.updateUserPreferences({
      data: {
        settings: [
          { slug: SETTING_SLUG, value: JSON.stringify(newPreferences) },
        ],
      },
    })
  }

  /**
   * Debug helper. Values are stringified first, otherwise console.table
   * spreads object values across one column per key and the output becomes
   * unreadable. The `type` column calls out booleans and dates that were
   * stored as strings, since `"false"` is truthy and easy to misread.
   *
   * @returns the raw preferences, so the console shows them expandable
   * below the table for anything the table truncates
   */
  list() {
    const describeType = (value: unknown) => {
      if (Array.isArray(value)) return "array"
      if (typeof value !== "string") return typeof value
      if (value === "true" || value === "false") return "string (boolean)"
      if (!Number.isNaN(Date.parse(value)) && /^\d{4}-\d{2}-\d{2}/.test(value))
        return "string (date)"
      return "string"
    }

    const rows = Object.fromEntries(
      Object.entries(this._currentUserPrefs)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => [
          key,
          {
            type: describeType(value),
            value: typeof value === "string" ? value : JSON.stringify(value),
          },
        ])
    )

    const authInfo = AuthManager.getPlatformAuth()
    console.log(
      `${Object.keys(rows).length} preferences · sync ${
        this._enableSync ? "on" : "off"
      } · user ${authInfo?.user.uuid ?? "unknown"}`
    )
    console.table(rows, ["type", "value"])

    return this._currentUserPrefs
  }
}

const UserPreferenceManager = new _UserPreferenceManager()

if (
  IS_LOCALHOST ||
  IS_DEV_ENVIRONMENT ||
  sessionStorage.getItem("debug") === "1"
) {
  // @ts-expect-error safe to do
  window.UserPreferenceManager = UserPreferenceManager
}

export { UserPreferenceManager }
